import React, { useState } from 'react';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import format from 'date-fns/format';
import classNames from 'classnames';
import Tooltip from '../tooltip/tooltip';
import { sentenceCase } from 'change-case';
import { formatNumberWithCommas } from '../../../utilities/utilities';
import {
  EXCLUSIVE_JOB_TYPE_ID,
  STAGE_OPTIONS,
} from '../../../utilities/constants/job_related.constants';
import { toggleFavorite } from './services/job_item.service';
import { Props } from './types';
import styles from './job_item.module.scss';
import AlertPopup from '../alert_popup/alert_popup';
import { AlertDataType } from '../alert_popup/types';
import { GENERIC_ERROR_MESSAGE } from '../alert_popup/constants';

const plausibleUpgradeTracking = `
  plausible-event-name=click
  plausible-event-intention=paywall-join
  plausible-event-section=salary
`;

const JobItem = ({
  job,
  userInfo,
  returnToValue,
  featuredJobs,
  setFeaturedJobs,
  nonFeaturedJobs,
  setNonFeaturedJobs,
}: Props) => {
  const [alertData, setAlertData] = useState<AlertDataType | null>(null);

  const {
    is_featured,
    id,
    published_at,
    display_logo,
    job_type,
    name,
    slug_and_id,
    is_remote,
    closing_date,
    is_email,
    places = [],
    employer_company = {},
    stage,
    salary_data = {},
    show_upgrade_to_ca_banner,
    show_login_banner,
    is_favorite,
    career_level,
    news_topics,
  } = job;
  const { viewExclusiveJobs, isSignedIn } = userInfo;
  const NEW_JOB_APPLICATION_PATH = `/jobs/${id}/applications/new`;
  const loginPath = `/login?return_to=${returnToValue}`;
  const createFreeAccountPath = `/register/CM?return_to=${returnToValue}`;
  const paymentPagePermalink = 'payment_page_permalink=ca-trial-job';
  const upgradeToCaPath = `/register/CA?${paymentPagePermalink}&return_to=${returnToValue}`;

  const getSearchQueryFromSessionStorage = (): string | null => {
    try {
      return window.sessionStorage.getItem('searchQuery');
    } catch {
      return null;
    }
  };

  const searchResults = JSON.parse(getSearchQueryFromSessionStorage());
  const JOB_DETAILS_LINK =
    searchResults !== null
      ? `/jobs/${slug_and_id}?q=${searchResults.urlReferer}`
      : `/jobs/${slug_and_id}`;

  const stageOption = STAGE_OPTIONS.find((option) => option.value === sentenceCase(stage || ''));
  const newlyPublished = differenceInCalendarDays(new Date(), new Date(published_at)) <= 1;
  const notRecentlyPublished = differenceInCalendarDays(new Date(), new Date(published_at)) > 30;
  const isExclusiveJob = job_type === EXCLUSIVE_JOB_TYPE_ID;
  const titleUnavailable = isExclusiveJob && !viewExclusiveJobs;

  const handleToogleFavorite = () => {
    toggleFavorite(id, is_favorite)
      .then(() => {
        const affectedList = is_featured ? featuredJobs : nonFeaturedJobs;

        const updatedLit = affectedList.map((element) => {
          if (element.id !== id) return element;

          return {
            ...element,
            is_favorite: !is_favorite,
          };
        });

        return is_featured ? setFeaturedJobs(updatedLit) : setNonFeaturedJobs(updatedLit);
      })
      .catch(() => {
        setAlertData({
          type: 'error',
          message: GENERIC_ERROR_MESSAGE,
        });
      });
  };

  const renderExclusiveJobLabel: JSX.Element = (
    <div
      className={`${styles.memberJobIcon} position-absolute`}
      data-testid='member-job-icon'
      title={viewExclusiveJobs ? 'Unlocked for your membership' : 'Exclusive job'}
    >
      <i className={`fal icon-lock-${viewExclusiveJobs ? 'open-alt' : 'alt'}`} />
    </div>
  );

  const renderUpgradeToCaBanner: JSX.Element = (
    <div className='mt-2'>
      <div data-testid='upgrade-to-ca-banner' className={styles.salaryInformationPlaceholder}>
        <i className='fal icon-envelope-open-dollar me-1 text-gray-lig'></i>
        <span>
          <a
            href={upgradeToCaPath}
            className={`strong link-blue-orange ${plausibleUpgradeTracking}`}
          >
            Upgrade to a Career Account
          </a>{' '}
          to see this job&apos;s salary
        </span>
      </div>
    </div>
  );

  const renderLoginBanner: JSX.Element = (
    <div className='mt-2'>
      <div data-testid='login-banner' className={styles.salaryInformationPlaceholder}>
        <i className='fal icon-envelope-open-dollar me-1 text-gray-lig'></i>
        <span>
          Please{' '}
          <a href={loginPath} className='strong link-blue-orange'>
            login
          </a>{' '}
          or{' '}
          <a href={createFreeAccountPath} className='strong link-blue-orange'>
            create a free account{' '}
          </a>
          to see this job&apos;s salary
        </span>
      </div>
    </div>
  );

  const renderSalaryData: JSX.Element = (
    <div className='mt-2'>
      <div data-testid='salary-information'>
        <i className='fal icon-envelope-open-dollar me-1 text-gray-lig'></i>
        <span>
          {salary_data?.unique_salary_value ? (
            <span> {formatNumberWithCommas(salary_data.unique_salary_value)} </span>
          ) : (
            <span>
              {' '}
              {formatNumberWithCommas(salary_data.range_bottom)} -{' '}
              {formatNumberWithCommas(salary_data.range_top)}{' '}
            </span>
          )}
          {salary_data.currency}
        </span>
        <span className='ms-1 small'>({salary_data.periodicity})</span>
      </div>
    </div>
  );

  const renderPublishedAtInfo: JSX.Element = (
    <div
      data-testid='published-date-section'
      className={classNames(`${styles.publishedDate} mt-1`, {
        [styles.new]: newlyPublished,
        [styles.lighter]: notRecentlyPublished,
      })}
    >
      {newlyPublished ? 'New' : formatDistanceToNow(new Date(published_at), { addSuffix: true })}
    </div>
  );

  const renderCompanyLogo: JSX.Element = (
    <img
      className={`${styles.companyLogo} p-1 position-relative`}
      alt='company logo'
      src={`https://res.cloudinary.com/devex/image/fetch/c_pad,f_auto,q_auto,h_75,w_75/${display_logo}`}
    />
  );

  const renderDefaultCompanyLogo: JSX.Element = (
    <div className={`${styles.companyLogo} ${styles.placeholder} p-1 position-relative hidden-xs`}>
      <i className='fal icon-building mt-3 d-block' />
    </div>
  );

  const renderJobPlaces: JSX.Element = (
    <span className='text-gray-li' data-testid='job-places-section'>
      <span data-testid='job-places-value'>
        {places[0]?.place_name}
        {places.length > 1 ? ' ... ' : ''}
      </span>
      {is_remote && <span className='ms-1'>(Remote position)</span>}
    </span>
  );

  const renderClosingDate: JSX.Element = new Date(closing_date).toString() !== 'Invalid Date' && (
    <div className='col-12 col-md-4 col-lg-6 pe-0 mt-4 mt-md-1' data-testid='closing-date'>
      <span
        className={classNames({
          'text-orange-dark': differenceInCalendarDays(new Date(closing_date), new Date()) <= 7,
        })}
      >
        <i className='fal icon-clock text-gray-li' />
        <span className='ms-1'>Closing on {format(new Date(closing_date), 'MMM d, yyyy')}</span>
      </span>
    </div>
  );

  const renderStage: JSX.Element = (
    <Tooltip placement='top' message={stageOption?.tooltip || ''}>
      <span className='pt-1'>
        <i className='fal icon-info-circle text-gray-li' />
        <span className='ms-1'>{stageOption?.value}</span>
      </span>
    </Tooltip>
  );

  const renderFavoriteForSignedInUsers: JSX.Element = (
    <>
      <div className='btn btn-outline-secondary btn-sm' onClick={handleToogleFavorite}>
        <i
          className={classNames('fal icon-star me-1', { 'text-orange-devex': is_favorite })}
          title='Mark as favorite'
        />
        <span className={classNames({ 'text-orange-devex': is_favorite })}>
          {is_favorite ? 'Saved' : 'Save'}
        </span>
      </div>
      {is_email && !show_upgrade_to_ca_banner && (
        <a
          href={NEW_JOB_APPLICATION_PATH}
          className={`${styles.quickApply} btn btn-link btn-sm link-orange-dark-devex`}
        >
          {devex?.flipper.new_logo ? (
            <img
              className={`${styles.iconDevex} pe-1`}
              aria-label='devex-logo'
              src='https://res.cloudinary.com/devex/image/asset/d_icon_orange-84318d9b74bfc1d5542f70b0721ff924.png'
              height='20'
              alt='Devex Exclusive Job Logo'
            />
          ) : (
            <img
              className={`${styles.iconDevex} pe-1`}
              aria-label='devex-logo'
              src='https://res.cloudinary.com/devex/image/asset/
  d_icon_orange-da9177fd20a19ce6fb250c60440f75ba.svg'
              height='14'
              width='11'
            />
          )}
          <strong>Easy apply</strong>
        </a>
      )}
    </>
  );

  const renderFavoriteForUA: JSX.Element = (
    <Tooltip placement='top' message='Sign in to add it to your favorites.'>
      <span className='btn btn-outline-secondary btn-sm disabled' aria-disabled='true'>
        <i className='fal icon-star favorite me-1' />
        <span>Save</span>
      </span>
    </Tooltip>
  );

  const renderSalarySection = (): JSX.Element | null => {
    if (show_upgrade_to_ca_banner) return renderUpgradeToCaBanner;
    if (show_login_banner) return renderLoginBanner;
    return Object.keys(salary_data).length ? renderSalaryData : null;
  };

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div
        className={classNames(`${styles.jobItem} position-relative py-4 px-0 border-0`, {
          featured: is_featured,
        })}
      >
        {isExclusiveJob && renderExclusiveJobLabel}
        <div className='w-100 d-table'>
          <div className='pe-4 text-center d-none d-sm-inline-block'>
            {display_logo ? renderCompanyLogo : renderDefaultCompanyLogo}
            {is_featured ? (
              <div className={`${styles.featuredTag} pb-1 mt-1 fw-bold`}>Featured</div>
            ) : (
              <div className='date'>{renderPublishedAtInfo}</div>
            )}
          </div>
          <div className='d-table-cell align-top h-100 w-100' data-testid='job-info-container'>
            {is_featured ? (
              <div className={`${styles.featuredTag} pb-1 mt-1 fw-bold d-inline-block d-sm-none`}>
                Featured
              </div>
            ) : (
              <div className='date mb-1 d-inline-block d-sm-none'>{renderPublishedAtInfo}</div>
            )}
            <div className='mb-1 pe-45' data-testid='job-details-page-link'>
              <a className='link-blue-orange' href={JOB_DETAILS_LINK}>
                {titleUnavailable
                  ? `${career_level?.name} position in ${news_topics?.[0]?.name}`
                  : name}
              </a>
            </div>
            <div className='secondary-info'>
              <span>{employer_company?.name}</span>

              {Boolean(places.length) && employer_company?.name && <span> · </span>}
              {Boolean(places.length) && renderJobPlaces}
            </div>
            {renderSalarySection()}
            <div className='row small text-gray favorite-container'>
              <div className='col-12 col-md-5 col-lg-4'>
                <div className={styles.favoriteToggle}>
                  {isSignedIn ? renderFavoriteForSignedInUsers : renderFavoriteForUA}
                </div>
              </div>
              {renderClosingDate}
              <div className='col-12 col-md-3 col-lg-2 mt-2 pe-0'>
                {stage && stageOption && renderStage}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default JobItem;
